/**
 * HTA (Hipertensión Arterial) Pathology Configuration
 *
 * This file contains the complete configuration for the HTA pathology,
 * including modality-specific settings for Protocol and Maintenance modes.
 */

import {
  htaProtocolGlossaryData,
  htaMaintenanceGlossaryData,
} from "../glossaryData";

export const HTA_ID = 2;

// HTA Modalities
export const HTA_MODALITY_IDS = {
  PROTOCOL: 1,
  MAINTENANCE: 2,
};

export const htaConfig = {
  id: HTA_ID,
  name: "HTA",
  displayNameKey: "hta", // i18n translation key

  // Feature flags - determines what features are available for this pathology
  // Available features:
  // surveys, ecg, nutrition, howIFeel, modalities, healthBackground, medicalInformation, configParameters, customizeFrequency
  // Only include features that are enabled (presence = enabled, absence = disabled)
  features: [
    "nutrition",
    "modalities",
    "hasModality",
    "healthBackground",
    "medicalInformation",
    "customizeFrequency",
    "glossary",
    "healthData",
  ],

  components: {
    medicalInfoForm: "ModifyMedicalInformationHTA",
  },

  // HTA has modality-specific configurations
  modalities: {
    [HTA_MODALITY_IDS.PROTOCOL]: {
      name: "Protocol",
      displayName: "Protocolo",
      glossaryData: htaProtocolGlossaryData,
      breadcrumbTitle: "protocol_table",
      columnSet: "hta-protocol",
      exportFields: [
        "fullname",
        "antiquity",
        "weight",
        "measurement_dates",
        "count_measurements",
        "bp_morning_average",
        "bp_afternoon_average",
        "bp_weekly_average",
        "hr_morning_afternoon",
        "hr_general_average",
      ],
      exportTransformer: (row, t) => [
        row.fullname,
        row.antiquity,
        row.weight ? row.weight : "--",
        row.bp_values.measurement_dates.value,
        row.count_measurements,
        row.bp_values.bp_morning_average.value,
        row.bp_values.bp_afternoon_average.value,
        row.bp_values.bp_weekly_average.value,
        row.hr_values.hr_morning_afternoon,
        row.hr_values.hr_general_average,
      ],
      // Chart configurations - references chart definitions from chartDefinitions.js
      charts: [
        'hta-general-blood-pressure',
        'hta-general-heart-rate',
        'hta-morning-blood-pressure',
        'hta-morning-heart-rate',
        'hta-afternoon-blood-pressure',
        'hta-afternoon-heart-rate',
      ],
    },
    [HTA_MODALITY_IDS.MAINTENANCE]: {
      name: "Maintenance",
      displayName: "Mantenimiento",
      glossaryData: htaMaintenanceGlossaryData,
      breadcrumbTitle: "maitenance_table",
      columnSet: "hta-maintenance",
      exportFields: [
        "fullname",
        "antiquity",
        "weight",
        "count_measurements",
        "bp_average",
        "hr_average",
        "bp_max",
        "hr_max",
        "sys_out",
        "dias_out",
      ],
      exportTransformer: (row, t) => [
        row.fullname,
        row.antiquity,
        row.weight ? row.weight : "--",
        row.count_measurements,
        row.bp_values.bp_average,
        row.hr_values.hr_average,
        row.bp_values.bp_max,
        row.hr_values.hr_max,
        row.bp_values.sys_out,
        row.bp_values.dias_out,
      ],
      // Chart configurations - references chart definitions from chartDefinitions.js
      // Maintenance mode reuses common charts (same as ICC)
      charts: [
        'blood-pressure',
        'heart-rate',
      ],
    },
  },

  dashboard: {
    exportFields: [], // Use modality-specific fields
    exportTransformer: null, // Use modality-specific transformer
  },

  profileFields: {
    weight: true,
    height: false,
    bmi: false,
    bloodPressure: true,
    heartRate: true,
    ejectionFraction: false,
  },

  alerts: {
    showHowIFeelColumn: false,
    priorityLevels: [1, 2, 3, 4, 5],
  },
};

export default htaConfig;
